import type { FC } from "react";
import { useTranslation } from "react-i18next";

import { Typography } from "@aviary";
import { l } from "@shared/locales/i18n";
import type { PractitionerAdditionalAttributesType } from "@shared/types/PractitionerAdditionalAttributesType";

import GoogleSVG from "./assets/GoogleG.svg";

import * as styles from "./GoogleSSOButton.styles";

interface Props {
  treatmentPlanSlug?: string;
  labRecommendationId?: string;
  storeSlug?: string;
  pracTypeId?: string;
  signUpPage?: string;
  pageVersion?: string;
  disabled?: boolean;
  isOrTextRendered?: boolean;
  additionalAttributes?: PractitionerAdditionalAttributesType;
}

const GoogleSSOButton: FC<Props> = ({
  signUpPage,
  treatmentPlanSlug,
  labRecommendationId,
  storeSlug,
  pracTypeId,
  pageVersion,
  disabled,
  isOrTextRendered,
  additionalAttributes,
  ...rest
}) => {
  const { t } = useTranslation();
  const csrf = document.querySelector("meta[name='csrf-token']")?.getAttribute("content");
  const buttonText = t(l.googleSSO.ContinueWithGoogle);

  const formStyles = [styles.form, disabled && styles.formDisabled];
  const submitStyles = [styles.submit, disabled && styles.submitDisabled];
  const logoStyles = [styles.logo, disabled && styles.disabled];
  const googleSSOButtonStyles = [styles.GoogleSSOButton, disabled && styles.disabled];

  const {
    referralCode,
    referralPartnerId,
    referralCodeId,
    micrositeId,
    redirectPath,
    signupSource,
    apiClientId,
    isOauth,
    source,
    peerReferralCode,
    peerReferrerSlug,
  } = additionalAttributes || {};

  const getActionURL = () => {
    const urlSearchParams = new URLSearchParams();
    if (pageVersion) urlSearchParams.set("page_version", pageVersion);
    if (signUpPage) urlSearchParams.set("sign_up_page", signUpPage);
    if (treatmentPlanSlug) urlSearchParams.set("treatment_plan_slug", treatmentPlanSlug);
    if (labRecommendationId) urlSearchParams.set("lab_recommendation_id", labRecommendationId);
    if (storeSlug) urlSearchParams.set("store_slug", storeSlug);
    if (pracTypeId) urlSearchParams.set("practitioner_type_id", pracTypeId);
    if (referralCode) urlSearchParams.set("referral_code", referralCode);
    if (referralPartnerId) urlSearchParams.set("referral_partner_id", referralPartnerId);
    if (referralCodeId) urlSearchParams.set("referral_code_id", referralCodeId);
    if (micrositeId) urlSearchParams.set("microsite_id", micrositeId);
    if (peerReferralCode) urlSearchParams.set("peer_referral_code", peerReferralCode);
    if (peerReferrerSlug) urlSearchParams.set("peer_referrer_slug", peerReferrerSlug);
    if (redirectPath) urlSearchParams.set("redirect_path", redirectPath);
    if (signupSource) urlSearchParams.set("signup_source", signupSource);
    if (apiClientId) urlSearchParams.set("api_client_id", apiClientId);
    if (isOauth) urlSearchParams.set("is_oauth", isOauth);
    if (source) urlSearchParams.set("source", source);
    return `/user/auth/google_oauth2?${urlSearchParams.toString()}`;
  };

  return (
    <>
      <link rel="preconnect" href="https://fonts.gstatic.com"></link>
      <link
        href="https://fonts.googleapis.com/css2?family=Roboto:wght@500&display=swap"
        rel="stylesheet"
      ></link>
      <div css={styles.buttonWrapper} data-e2e="new-sign-in-flow">
        <div css={formStyles} {...rest}>
          <form action={getActionURL()} method="post">
            <label aria-label={buttonText} css={googleSSOButtonStyles} htmlFor={buttonText}>
              <GoogleSVG css={logoStyles} />
              <input
                id={buttonText}
                value={buttonText}
                type="submit"
                css={submitStyles}
                disabled={disabled}
              />
            </label>
            <input type="hidden" name="authenticity_token" value={csrf}></input>
          </form>
        </div>
        {isOrTextRendered && (
          <Typography type="footnote" isMarginless css={styles.orSection}>
            {t(l.googleSSO.Or)}
          </Typography>
        )}
      </div>
    </>
  );
};

export { GoogleSSOButton };
